import { Plus, RefreshCw, Pencil } from "lucide-react";
import { btnPrimaryClass, btnSecondaryClass, btnSecondarySmClass, inputBaseClass } from "./constants";
import { fmtNotificationDate } from "./formatDate";
import { ApiResponsePre, FormLabel, FormSectionCard, SelectField } from "./shared";
import { EditWebhookModal } from "./EditWebhookModal";
import { WebhookOAuthFields, isOAuthAuthType } from "./WebhookOAuthFields";
import {
  authBadge,
  statusBadge,
  isAuthedWebhook,
  resolveAuthTypes,
  OAUTH2_AUTH_TYPE,
  emptyOAuthConfig
} from "./webhookHelpers";

export function WebhookRegistryTab({
  webhooks,
  integrations,
  whName,
  setWhName,
  whUrl,
  setWhUrl,
  whAuthType,
  setWhAuthType,
  whOAuthConfig,
  setWhOAuthConfig,
  whFieldErrors,
  whResponse,
  loadWebhooks,
  onCreateWebhook,
  onToggleWebhook,
  onDeleteWebhook,
  onPatchWebhook,
  editingWebhook,
  setEditingWebhook,
  whPatchSaving,
  isWebhookAdmin
}) {
  // Always list every auth type from GET /webhook-registry/integrations (never hide OAuth2 in UI).
  const authTypes = resolveAuthTypes(integrations);

  const showOAuthBlock = isOAuthAuthType(whAuthType);
  const defaultAuthType = authTypes.find((a) => a.value === "none")?.value ?? "none";

  const resetAddForm = () => {
    setWhName("");
    setWhUrl("");
    setWhAuthType(defaultAuthType);
    setWhOAuthConfig(emptyOAuthConfig());
  };

  return (
    <>
      <div className="mb-5">
        <FormSectionCard
          title="Add Webhook"
          subtitle="Register a destination URL that will receive LexAI event notifications."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
            <div>
              <FormLabel htmlFor="wh-name" required>
                Webhook Name
              </FormLabel>
              <input
                id="wh-name"
                className={`${inputBaseClass} ${whFieldErrors.name ? "border-red-400" : ""}`}
                value={whName}
                onChange={(e) => setWhName(e.target.value)}
                placeholder="e.g. My Slack Alerts"
              />
              {whFieldErrors.name ? (
                <p className="text-[11px] text-red-600 mt-1">{whFieldErrors.name}</p>
              ) : null}
            </div>
            <div>
              <FormLabel htmlFor="wh-url" required>
                Webhook URL
              </FormLabel>
              <input
                id="wh-url"
                className={`${inputBaseClass} ${whFieldErrors.webhook_url ? "border-red-400" : ""}`}
                value={whUrl}
                onChange={(e) => setWhUrl(e.target.value)}
                placeholder="https://your-server.com/webhook"
              />
              {whFieldErrors.webhook_url ? (
                <p className="text-[11px] text-red-600 mt-1">{whFieldErrors.webhook_url}</p>
              ) : null}
            </div>
          </div>

          <div className="mt-4">
            <FormLabel htmlFor="wh-auth">Authentication</FormLabel>
            <SelectField
              id="wh-auth"
              value={whAuthType}
              hasError={Boolean(whFieldErrors.auth_type)}
              onChange={(e) => {
                setWhAuthType(e.target.value);
                if (!isOAuthAuthType(e.target.value)) {
                  setWhOAuthConfig({
                    token_url: "",
                    scopes: "",
                    client_id: "",
                    client_secret: ""
                  });
                }
              }}
            >
              {authTypes.map((a) => (
                <option
                  key={a.value}
                  value={a.value}
                  disabled={a.value === OAUTH2_AUTH_TYPE && !isWebhookAdmin}
                >
                  {a.label}
                  {a.value === OAUTH2_AUTH_TYPE && !isWebhookAdmin ? " (admin)" : ""}
                </option>
              ))}
            </SelectField>
            {whFieldErrors.auth_type ? (
              <p className="text-[11px] text-red-600 mt-1">{whFieldErrors.auth_type}</p>
            ) : null}
          </div>

          {showOAuthBlock ? (
            <WebhookOAuthFields
              mode="create"
              config={whOAuthConfig}
              onChange={setWhOAuthConfig}
              errors={whFieldErrors}
              requiredFields={
                integrations?.auth_types?.find((a) => a.value === whAuthType)
                  ?.required_auth_config_fields ?? []
              }
            />
          ) : null}

          <div className="mt-6 flex justify-end gap-2">
            {showOAuthBlock ? (
              <button type="button" onClick={resetAddForm} className={btnSecondaryClass}>
                Cancel
              </button>
            ) : null}
            <button type="button" onClick={onCreateWebhook} className={btnPrimaryClass}>
              <Plus className="w-4 h-4" />
              Add Webhook
            </button>
          </div>

          <ApiResponsePre ok={whResponse?.ok} text={whResponse?.text} />
        </FormSectionCard>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-[15px] font-semibold text-gray-900">Registered Webhooks</h2>
            <p className="text-xs text-gray-500 mb-0">
              Your organization&apos;s webhook endpoints
            </p>
          </div>
          <button type="button" onClick={() => loadWebhooks()} className={btnSecondarySmClass}>
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Name
                </th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  URL
                </th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Auth
                </th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Status
                </th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Created
                </th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {!webhooks.length ? (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-gray-400">
                    No webhooks yet. Add one above.
                  </td>
                </tr>
              ) : (
                webhooks.map((w) => {
                  const auth = authBadge(w);
                  const status = statusBadge(w.is_active);
                  const deleteDisabled = isAuthedWebhook(w) && !isWebhookAdmin;

                  return (
                    <tr key={w.id} className="border-b border-gray-100 hover:bg-slate-50/80">
                      <td className="py-3 px-3 font-semibold text-gray-900">{w.name}</td>
                      <td
                        className="py-3 px-3 font-mono text-xs text-gray-600 max-w-[220px] truncate"
                        title={w.webhook_url}
                      >
                        {w.webhook_url}
                      </td>
                      <td className="py-3 px-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${auth.className}`}
                        >
                          {auth.label}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${status.className}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              w.is_active ? "bg-green-500" : "bg-gray-400"
                            }`}
                          />
                          {status.label}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-xs text-gray-500">
                        {fmtNotificationDate(w.created_at)}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            onClick={() => setEditingWebhook(w)}
                            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50 inline-flex items-center gap-1"
                          >
                            <Pencil className="w-3 h-3" />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => onToggleWebhook(w.id, w.is_active)}
                            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-gray-50 hover:bg-gray-100"
                          >
                            {w.is_active ? "Disable" : "Enable"}
                          </button>
                          <button
                            type="button"
                            onClick={() => !deleteDisabled && onDeleteWebhook(w.id, w.name)}
                            disabled={deleteDisabled}
                            title={deleteDisabled ? "Admin only" : undefined}
                            className={`text-xs px-2 py-1 rounded-md border ${
                              deleteDisabled
                                ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                                : "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                            }`}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingWebhook ? (
        <EditWebhookModal
          webhook={editingWebhook}
          integrations={integrations}
          saving={whPatchSaving}
          onClose={() => setEditingWebhook(null)}
          onSave={onPatchWebhook}
        />
      ) : null}
    </>
  );
}
